import React, { useMemo } from 'react';

import ChannelCharts from '@components/shared/analytics/ChannelCharts';
import FilterBar from '@components/shared/analytics/FilterBar';
import InventoryHealthTable from '@components/shared/analytics/InventoryHealthTable';
import KpiCards from '@components/shared/analytics/KpiCards';
import TopProductsTable from '@components/shared/analytics/TopProductsTable';
import TrendChart from '@components/shared/analytics/TrendChart';
import { useAdminNotification } from '@contexts/admin/AdminNotificationContext';
import { useMerchant } from '@contexts/admin/MerchantContext';
import useOrderAnalytics from '@hooks/shared/analytics/useOrderAnalytics';
import ListPageHeader from '@shared/ListPageHeader';
import { Space, Alert, Progress, Spin } from 'antd';
import { useTranslation } from 'react-i18next';

import adminApiFetch from '@services/admin/apiService';

/**
 * Admin 訂單銷量分析頁面
 */
const OrderAnalytics = () => {
  const { t } = useTranslation();
  const { merchant, channels, currencies, loading: merchantLoading } = useMerchant();
  const { addJob } = useAdminNotification();

  const { loading, data, error, progress, query } = useOrderAnalytics({
    apiFetch: adminApiFetch,
    addJob,
  });

  // 建立 channelMap
  const channelMap = useMemo(() => {
    const map = {};
    channels.forEach((channel) => {
      map[channel.id] = {
        id: channel.id,
        name: channel.name,
      };
    });
    return map;
  }, [channels]);

  const handleQuery = (params) => {
    query(params);
  };

  // 如果 MerchantContext 還在載入中，顯示載入狀態
  if (merchantLoading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果沒有 channels，顯示提示
  if (!channels || channels.length === 0) {
    return (
      <div style={{ padding: '0 0 24px 0' }}>
        <ListPageHeader basePath="/admin" items={['analytics.title', 'analytics.orders']} />
        <div style={{ padding: '0 24px' }}>
          <Alert
            message={t('shared.analytics.no_channels')}
            description={t('shared.analytics.no_channels_description')}
            type="warning"
            showIcon
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      <ListPageHeader basePath="/admin" items={['analytics.title', 'analytics.orders']} />

      <div style={{ padding: '0 24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 篩選條件 */}
          <FilterBar
            channels={channels}
            currencies={currencies}
            defaultCurrency={merchant?.default_currency || 'TWD'}
            onQuery={handleQuery}
            loading={loading}
          />

          {/* 進度提示 */}
          {loading && progress && (
            <Alert
              message={t(progress.message)}
              description={
                <Progress
                  percent={((progress.current / progress.total) * 100).toFixed(0)}
                  status="active"
                />
              }
              type="info"
              showIcon
            />
          )}

          {/* 錯誤提示 */}
          {error && <Alert message={t('common.error.load_failed')} description={error} type="error" showIcon />}

          {/* 載入中 */}
          {loading && !progress && (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Spin size="large" />
            </div>
          )}

          {/* 數據展示 */}
          {data && !loading && (
            <>
              {/* KPI 卡片 */}
              <KpiCards data={data} />

              {/* 趨勢圖表 */}
              {data.time_series && data.time_series.length > 0 && (
                <TrendChart
                  timeSeries={data.time_series}
                  channelMap={channelMap}
                  metadata={data.metadata}
                />
              )}

              {/* 通路分析 */}
              <ChannelCharts data={data} channelMap={channelMap} />

              {/* 熱銷商品表格 */}
              {data.top_products && (
                <TopProductsTable products={data.top_products} channelMap={channelMap} metadata={data.metadata} />
              )}

              {/* 庫存健康度 */}
              {data.inventory_health && (
                <InventoryHealthTable inventoryHealth={data.inventory_health} />
              )}
            </>
          )}
        </Space>
      </div>
    </div>
  );
};

export default OrderAnalytics;
